import { Component } from '@theme/component';

/**
 * @typedef {Object} CustomRingCarouselRefs
 * @property {HTMLElement} viewport - The clipping viewport
 * @property {HTMLElement} track - The flex track that translates
 * @property {HTMLElement[]} slides - The card slides
 * @property {HTMLButtonElement} [previousButton]
 * @property {HTMLButtonElement} [nextButton]
 */

/**
 * Below this many products, there's nothing to page through — the whole group
 * fits the viewport's usual preview width at once. In that case the carousel
 * renders as a static, centered row instead: no arrows, no dragging, no sliding.
 */
const MIN_SLIDES_FOR_CAROUSEL = 4;

/**
 * Desktop shows this many whole cards at once (or fewer if there aren't that
 * many products). At the fixed 278px Figma card width, only 3 whole cards fit
 * this section's container without cropping a neighbor — too sparse. Sizing
 * each card to the available width instead keeps every card whole AND keeps
 * this density. Mobile is unaffected — it keeps its own fixed 70vw
 * single-card width.
 *
 * Kept in sync with the `/ 5` divisor in --ring-card-width in
 * custom-ring-carousel.css, which does this same sizing in pure CSS via
 * container query units — no JS needed for the common case, and so no flash
 * of the old fixed-width card before JS resized it down on page load.
 * #fitCardsToViewport only overrides the CSS value when there are fewer than
 * this many products.
 */
const TARGET_VISIBLE_CARDS = 5;

/** Matches the breakpoint in custom-ring-carousel.css where --ring-card-width
 *  switches from the fixed desktop size to the mobile 70vw single-card size. */
const DESKTOP_BREAKPOINT = '(min-width: 750px)';

/**
 * Ring gallery carousel — centers the active card within the viewport and steps
 * with the arrow buttons one product at a time. Stepping stops at the first/last
 * product (no wraparound); the arrows disable there instead, and hide entirely
 * once there's nothing at all left to page to (see #updateArrowState) — which on
 * desktop is whenever the product count fits within TARGET_VISIBLE_CARDS, since
 * the CSS sizes cards to fit that many at once; mobile only ever fits one card at
 * a time, so its arrows stay regardless of product count. Card width is sized
 * (in CSS, see custom-ring-carousel.css) to fit TARGET_VISIBLE_CARDS exactly; the
 * viewport's width is then trimmed by #updateViewportWidth to an exact multiple
 * of card-plus-gap. Together, a neighboring card is never sliced in half at the
 * edges — only whole cards are ever visible. Figma 338:5648.
 * @extends {Component<CustomRingCarouselRefs>}
 */
class CustomRingCarousel extends Component {
  #current = 0;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {AbortController | undefined} */
  #swipeController;
  /** Minimum horizontal travel (px) to register as a swipe */
  #swipeThreshold = 40;
  #pointerStartX = 0;
  #pointerStartY = 0;
  #pointerId = null;
  /** True once a gesture is confirmed horizontal and the track is following the finger */
  #dragging = false;
  /** Track offset (px) when the current drag started */
  #dragBaseOffset = 0;
  /** #current when the drag started */
  #dragBaseIndex = 0;
  /** Last offset applied to the track, so a drag can pick up from the resting position */
  #currentOffset = 0;

  connectedCallback() {
    super.connectedCallback();

    const { previousButton, nextButton } = this.refs;
    if (this.#count < MIN_SLIDES_FOR_CAROUSEL) {
      // Too few products to page through — render as a static centered row instead
      // (see .ring-carousel--static in custom-ring-carousel.css) and hide the arrows,
      // since there's nothing for them to do.
      this.classList.add('ring-carousel--static');
      if (previousButton) previousButton.hidden = true;
      if (nextButton) nextButton.hidden = true;
      return;
    }

    const requestedIndex = Number(this.dataset.startIndex) || 0;
    this.#current = this.#clamp(requestedIndex);

    this.#resizeObserver = new ResizeObserver(() => {
      this.#updateViewportWidth();
      this.#updateCenterPadding();
      this.#render(false);
      this.#updateArrowState();
    });
    this.#resizeObserver.observe(this);
    this.#bindSwipe();
    this.#updateViewportWidth();
    this.#updateCenterPadding();
    this.#render(false);
    this.#updateArrowState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#swipeController?.abort();
  }

  /** Bind touch/pointer swipe gestures on the viewport so mobile users can drag between cards. */
  #bindSwipe() {
    const { viewport } = this.refs;
    if (!viewport) return;

    this.#swipeController = new AbortController();
    const { signal } = this.#swipeController;

    viewport.addEventListener('pointerdown', this.#onPointerDown, { signal });
    viewport.addEventListener('pointermove', this.#onPointerMove, { signal });
    viewport.addEventListener('pointerup', this.#onPointerUp, { signal });
    viewport.addEventListener('pointercancel', this.#onPointerCancel, { signal });
    // Swallow the synthetic click that follows a drag so the card link doesn't navigate
    viewport.addEventListener('click', this.#onClick, { signal, capture: true });
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    this.#pointerId = event.pointerId;
    this.#pointerStartX = event.clientX;
    this.#pointerStartY = event.clientY;
    this.#dragBaseOffset = this.#currentOffset;
    this.#dragBaseIndex = this.#current;
    this.#dragging = false;
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (this.#pointerId !== event.pointerId) return;

    const deltaX = event.clientX - this.#pointerStartX;
    const deltaY = event.clientY - this.#pointerStartY;

    if (!this.#dragging) {
      // Wait until the gesture is clearly horizontal before hijacking it from page scroll
      if (Math.abs(deltaX) <= Math.abs(deltaY) || Math.abs(deltaX) < 6) return;
      this.#dragging = true;
      const { track, viewport } = this.refs;
      track.style.transition = 'none';
      viewport.setPointerCapture(event.pointerId);
    }

    // Track follows the finger 1:1; dragging left (negative deltaX) increases the offset
    this.#applyOffset(this.#dragBaseOffset - deltaX);
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (this.#pointerId !== event.pointerId) return;
    this.#pointerId = null;
    if (!this.#dragging) return;
    this.#dragging = false;

    const moved = event.clientX - this.#pointerStartX;
    const step = this.#stepWidth();
    // Snap to the nearest card, but honour a small flick past the threshold
    let indexDelta = step > 0 ? Math.round(-moved / step) : 0;
    if (indexDelta === 0 && Math.abs(moved) >= this.#swipeThreshold) {
      indexDelta = moved < 0 ? 1 : -1;
    }

    this.#current = this.#clamp(this.#dragBaseIndex + indexDelta);
    this.#render(true);
    this.#updateArrowState();
  };

  /** @param {PointerEvent} event */
  #onPointerCancel = (event) => {
    if (this.#pointerId !== event.pointerId) return;
    this.#pointerId = null;
    if (!this.#dragging) return;
    this.#dragging = false;
    this.#render(true);
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    // A tap navigates; a drag must not. Suppress the link click only when the finger travelled.
    const moved = Math.abs(event.clientX - this.#pointerStartX);
    if (moved < this.#swipeThreshold) return;
    event.preventDefault();
    event.stopPropagation();
  };

  /** Horizontal distance between adjacent cards (card width + gap). */
  #stepWidth() {
    const { slides } = this.refs;
    if (!slides || slides.length === 0) return 0;
    if (slides.length > 1) return slides[1].offsetLeft - slides[0].offsetLeft;
    return slides[0].offsetWidth;
  }

  /**
   * Narrows the viewport to hold exactly as many whole cards as should be
   * visible, then centers that viewport (via the CSS `margin-inline: auto` on
   * .ring-carousel__viewport). Without this, the viewport stayed 100% wide and
   * overflow:hidden clipped whatever fraction of a neighboring card happened
   * to land past the edge — a sliced-in-half card peeking in on both sides.
   *
   * On desktop, #fitCardsToViewport has already sized the cards to fit
   * TARGET_VISIBLE_CARDS exactly, so its returned count is used directly.
   * Below the breakpoint (fixed-size mobile cards), the visible count instead
   * has to be derived from how many of those fixed-size cards fit, floored to
   * a whole number.
   *
   * That floored mobile count must be odd (when fewer than all slides fit):
   * centering the active card puts it exactly mid-viewport, so an odd count
   * gives it the same number of whole neighbors on each side, flush with both
   * edges. An even count would leave the active card off-center by half a
   * card, so one side would still have room for a slice of one more card than
   * the other. When every slide already fits (nothing left to page through),
   * the odd requirement is skipped since there's nothing left to peek at.
   */
  #updateViewportWidth() {
    const { viewport, slides } = this.refs;
    if (!viewport || !slides || slides.length === 0) return;

    const count = slides.length;
    let visibleCount = null;

    if (window.matchMedia(DESKTOP_BREAKPOINT).matches) {
      // #fitCardsToViewport already picked the card width so that exactly this
      // many cards fill the space — reuse its count directly instead of
      // re-deriving one from the measured width below. Re-deriving it would
      // re-introduce rounding slop from the floor() in #fitCardsToViewport
      // (the measured card-plus-gap step ends up a hair under the exact
      // available-width / target ratio), which the odd-count rule below would
      // then read as "one more card almost fits" and correct back down by
      // two — undoing the fit and landing on 3 instead of the intended 5.
      visibleCount = this.#fitCardsToViewport(count);
    } else {
      // Below the breakpoint, fall back to the CSS-defined mobile card width
      // (70vw) instead of whatever desktop size was last set here.
      this.style.removeProperty('--ring-card-width');
    }

    const cardWidth = slides[0].offsetWidth;
    const step = this.#stepWidth() || cardWidth;

    if (visibleCount == null) {
      visibleCount = Math.max(1, Math.floor(this.clientWidth / step));
      if (visibleCount < count && visibleCount % 2 === 0) visibleCount -= 1;
      visibleCount = Math.max(1, Math.min(visibleCount, count));
    }

    // Measure the real rendered span of the visible cards instead of
    // reconstructing it as cardWidth * N + gap * (N - 1): cardWidth comes from
    // offsetWidth, which rounds to a whole pixel, and that rounding error
    // compounds once per card across the multiplication. Reading the actual
    // left/right edges of just the first and last visible card needs only two
    // measurements regardless of N, and getBoundingClientRect's fractional
    // (unrounded) values track exactly what the browser painted — so the
    // viewport is never clipped a pixel short of the true last card, which
    // read as a sliced-off right border on that card. A stale transform from
    // a previous render doesn't skew this: translateX shifts every card by
    // the same amount, so it cancels out of the left-to-right difference.
    const firstRect = slides[0].getBoundingClientRect();
    const lastRect = slides[visibleCount - 1].getBoundingClientRect();
    const visibleWidth = lastRect.right - firstRect.left;
    viewport.style.width = `${visibleWidth}px`;
  }

  /**
   * Sets --ring-card-width so that TARGET_VISIBLE_CARDS cards (or fewer, if
   * there aren't that many products) fill the available width exactly, gap
   * included — replacing the fixed 278px Figma card width on desktop.
   *
   * When there are at least TARGET_VISIBLE_CARDS products (the common case),
   * this is a no-op: the CSS in custom-ring-carousel.css already computes the
   * identical width via container query units, from the very first paint.
   * Touching the inline style here anyway would replace that exact CSS value
   * with a floor()'d (integer-pixel) one, a subpixel change that's pure
   * downside — it re-triggers layout for no visual gain.
   * @returns {number} The card count this was built for — the caller uses
   *   this directly as the visible count, rather than re-measuring.
   */
  #fitCardsToViewport(count) {
    const target = Math.max(1, Math.min(TARGET_VISIBLE_CARDS, count));
    if (target === TARGET_VISIBLE_CARDS) return target;

    const gapValue = getComputedStyle(this).getPropertyValue('--ring-card-gap');
    const gap = parseFloat(gapValue) || 0;
    const cardWidth = Math.floor((this.clientWidth - (target - 1) * gap) / target);
    if (cardWidth > 0) this.style.setProperty('--ring-card-width', `${cardWidth}px`);
    return target;
  }

  /**
   * True once every slide already fits within the viewport side by side — the
   * case #updateViewportWidth builds for on desktop whenever the product count
   * is within TARGET_VISIBLE_CARDS. When this is true, there is nothing to
   * center or page: #updateCenterPadding and #offsetBounds both short-circuit
   * on it instead of running their normal per-active-card centering math,
   * which is built around a DIFFERENT case — a viewport sized for one active
   * card with peeking neighbors — and would otherwise still carve out room to
   * "center" each individual card inside the now much wider (whole-group)
   * viewport, producing a scrollable range that looks real to
   * #updateArrowState but has nothing genuine behind it to page to.
   */
  #allSlidesFit() {
    const { viewport, slides } = this.refs;
    if (!viewport || !slides || slides.length === 0) return true;
    const cardWidth = slides[0].offsetWidth;
    const step = this.#stepWidth() || cardWidth;
    const gap = Math.max(0, step - cardWidth);
    const contentWidth = slides.length * cardWidth + Math.max(0, slides.length - 1) * gap;
    // +1px slack absorbs subpixel rounding between this measurement and the
    // width #updateViewportWidth set, so equal-width cases reliably read as
    // "fits" instead of teetering on a rounding error.
    return contentWidth <= viewport.clientWidth + 1;
  }

  /**
   * Give the track enough inline padding for every slide — including the first
   * and last — to have real room to reach the center, instead of running out
   * of track past the edges. This only needs to be generous, not pixel-exact:
   * the actual travel limits are derived per-slide in #render/#applyOffset via
   * #centerOffsetFor, not from this padding's size, so any small mismatch here
   * no longer freezes the track short of the true first/last position.
   */
  #updateCenterPadding() {
    const { track, viewport, slides } = this.refs;
    if (!track || !viewport || !slides || slides.length === 0) return;
    if (this.#allSlidesFit()) {
      // Nothing to center — the group already sits flush, filling the
      // viewport exactly. Padding here would just carve out empty space.
      track.style.paddingInlineStart = '0px';
      track.style.paddingInlineEnd = '0px';
      return;
    }
    const cardWidth = slides[0].offsetWidth;
    const pad = Math.max(0, (viewport.clientWidth - cardWidth) / 2);
    track.style.paddingInlineStart = `${pad}px`;
    track.style.paddingInlineEnd = `${pad}px`;
  }

  /** Offset that puts the given slide's own center under the viewport's center. */
  #centerOffsetFor(slide) {
    const { viewport } = this.refs;
    return slide.offsetLeft - (viewport.clientWidth - slide.offsetWidth) / 2;
  }

  /** The [min, max] offsets that keep the first and last slide from overshooting center. */
  #offsetBounds() {
    const { slides } = this.refs;
    const count = slides?.length ?? 0;
    if (count === 0 || this.#allSlidesFit()) return [0, 0];
    const min = Math.max(0, this.#centerOffsetFor(slides[0]));
    const max = Math.max(min, this.#centerOffsetFor(slides[count - 1]));
    return [min, max];
  }

  /** Clamp a raw offset to the valid scroll range and apply it to the track immediately. */
  #applyOffset(offset) {
    const { track } = this.refs;
    if (!track) return;
    const [minOffset, maxOffset] = this.#offsetBounds();
    const clamped = Math.max(minOffset, Math.min(offset, maxOffset));
    this.#currentOffset = clamped;
    track.style.transform = `translate3d(${-clamped}px, 0, 0)`;
  }

  previous() {
    this.#current = this.#clamp(this.#current - 1);
    this.#render(true);
    this.#updateArrowState();
  }

  next() {
    this.#current = this.#clamp(this.#current + 1);
    this.#render(true);
    this.#updateArrowState();
  }

  get #count() {
    return this.refs.slides?.length ?? 0;
  }

  #clamp(index) {
    const count = this.#count;
    if (count === 0) return 0;
    return Math.max(0, Math.min(index, count - 1));
  }

  /**
   * Disables previous/next at the first/last product — there's no wraparound to step
   * into — and hides both entirely once every product already fits the viewport at
   * once (min and max offset coincide, so there's nowhere left to page to). On
   * desktop this is what hides the arrows once TARGET_VISIBLE_CARDS covers the whole
   * product list (e.g. 5 products or fewer); on mobile, which only ever fits one card
   * at a time, there's still a real offset range, so the arrows stay put regardless of
   * product count. Re-run on every resize (see the ResizeObserver above), since
   * crossing the mobile/desktop breakpoint changes which of those is true.
   */
  #updateArrowState() {
    const { previousButton, nextButton } = this.refs;
    const [minOffset, maxOffset] = this.#offsetBounds();
    // >1px of slack, not just >0 — leaves no room for a sub-pixel rounding
    // difference between the two offsets to be misread as "one more page".
    const canPage = maxOffset - minOffset > 1;
    if (previousButton) {
      previousButton.hidden = !canPage;
      previousButton.disabled = this.#current <= 0;
    }
    if (nextButton) {
      nextButton.hidden = !canPage;
      nextButton.disabled = this.#current >= this.#count - 1;
    }
  }

  /**
   * @param {boolean} animate - Whether to animate the translation
   */
  #render(animate) {
    const { track, viewport, slides } = this.refs;
    const count = slides?.length ?? 0;
    if (!track || !viewport || count === 0) return;

    const active = slides[this.#current];
    if (!active) return;

    // Center using the active slide's own laid-out position (offsetLeft ignores the
    // track's transform, so this is the true position regardless of the carousel's
    // current translation), and bound it by the first/last slide's own centering
    // targets — not by `track.scrollWidth - viewport.clientWidth`. That bound only
    // matches the real centering targets if the padding from #updateCenterPadding
    // lines up with viewport/card measurements exactly; any small mismatch there
    // (rounding, a scrollbar, a subpixel gap) made the track run out of travel a
    // couple of cards before the real end — it froze there while #current kept
    // advancing, so the "active" card was no longer the one actually centered.
    // Deriving every bound the same way (#centerOffsetFor) removes that
    // dependency, so every index — including the last two — reaches true center.
    const [minOffset, maxOffset] = this.#offsetBounds();
    const target = this.#centerOffsetFor(active);
    const offset = Math.max(minOffset, Math.min(target, maxOffset));
    this.#currentOffset = offset;

    track.style.transition = animate ? '' : 'none';
    track.style.transform = `translate3d(${-offset}px, 0, 0)`;
    if (!animate) {
      // Force a synchronous layout flush so the browser commits this instant jump as
      // a real, settled frame right now, then restore the CSS-defined transition —
      // guards against any later same-tick style write elsewhere getting batched
      // together with this one and animating from the wrong starting position.
      void track.offsetHeight;
      track.style.transition = '';
    }
  }
}

if (!customElements.get('custom-ring-carousel')) {
  customElements.define('custom-ring-carousel', CustomRingCarousel);
}
